import { analytics } from './analytics';

export interface PendingPenSwitchRender {
    source: 'next' | 'previous';
    cageId: number;
    startedAt: number;
    overviewCacheWarm: boolean;
}

let pending: PendingPenSwitchRender | null = null;
let stopRouteWait: (() => void) | null = null;

function stopWaitingForRoute(): void {
    stopRouteWait?.();
    stopRouteWait = null;
}

function publish(current: PendingPenSwitchRender): void {
    pending = null;
    stopWaitingForRoute();
    analytics.penSwitchRendered({
        source: current.source,
        durationMs: Math.round(performance.now() - current.startedAt),
        overviewCacheWarm: current.overviewCacheWarm,
        cageId: current.cageId,
    });
}

/**
 * The route channel is written by a parent effect, which runs after the dashboard
 * effect that reports a cache-warm paint. Publishing before that channel names the
 * destination would attach the timing to the pen being left.
 */
function publishWhenRouteMatches(): void {
    if (pending == null || analytics.routeCageId() !== pending.cageId) {
        return;
    }
    publish(pending);
}

/**
 * A next/previous step on the cage dashboard, timed until that pen's overview paints.
 *
 * `pen_switched` stays the click. Duration is only known after the destination
 * dashboard has rendered, so it rides a follow-up event rather than a second
 * `pen_switched`.
 */
export function beginPenSwitchRender(input: {
    source: 'next' | 'previous';
    cageId: number;
    overviewCacheWarm: boolean;
}): void {
    stopWaitingForRoute();
    pending = {
        source: input.source,
        cageId: input.cageId,
        overviewCacheWarm: input.overviewCacheWarm,
        startedAt: performance.now(),
    };
}

/**
 * The destination dashboard's overview has rendered for this pen.
 * Captures only once the route context is that pen, and names it on the event.
 */
export function completePenSwitchRender(cageId: number): void {
    if (pending == null || pending.cageId !== cageId) {
        return;
    }
    if (analytics.routeCageId() === cageId) {
        publish(pending);
        return;
    }
    if (stopRouteWait != null) {
        return;
    }
    stopRouteWait = analytics.subscribeRouteContext(publishWhenRouteMatches);
}

/** The reader left the dashboard before its overview rendered. */
export function cancelPenSwitchRender(): void {
    pending = null;
    stopWaitingForRoute();
}

/**
 * Drop a measurement whose destination is no longer the pen on screen.
 * Arriving at that pen keeps the step that just started.
 */
export function cancelPenSwitchRenderUnlessCage(cageId: number): void {
    if (pending != null && pending.cageId !== cageId) {
        cancelPenSwitchRender();
    }
}

export function resetPenSwitchRenderForTesting(): void {
    pending = null;
    stopWaitingForRoute();
}
